class MonCartDelivery extends HTMLElement {
  connectedCallback() {
    this.render();
    this.interval = window.setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  getParisDate() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.dataset.timeZone || 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), 12));
  }

  addBusinessDays(date, amount) {
    const result = new Date(date.getTime());
    let remaining = Math.max(0, Number(amount) || 0);
    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      const day = result.getUTCDay();
      if (day !== 0 && day !== 6) remaining -= 1;
    }
    return result;
  }

  render() {
    const output = this.querySelector('[data-mon-delivery-title]');
    if (!output) return;

    const preparation = Number(this.dataset.preparationDays) || 2;
    const transitMin = Number(this.dataset.transitMin) || 3;
    const transitMax = Math.max(transitMin, Number(this.dataset.transitMax) || 5);
    const prepared = this.addBusinessDays(this.getParisDate(), preparation);
    const earliest = this.addBusinessDays(prepared, transitMin);
    const latest = this.addBusinessDays(prepared, transitMax);
    const locale = this.dataset.locale || 'fr';
    // `data-format="short"` sert le bloc compact du tiroir : « lun. 31 août »
    // plutôt que « lundi 31 août ». Le formateur est mis en cache, `render` étant
    // rappelé à chaque minute et à chaque re-rendu du panier.
    const short = this.dataset.format === 'short';
    const cacheKey = `${locale}:${short}`;

    if (this.formatterKey !== cacheKey) {
      this.formatterKey = cacheKey;
      this.formatter = new Intl.DateTimeFormat(locale, {
        timeZone: 'UTC',
        weekday: short ? 'short' : 'long',
        day: 'numeric',
        month: short ? 'short' : 'long',
      });
    }

    const formatter = this.formatter;
    const template = this.getAttribute('data-template') || '[earliest] – [latest]';
    output.textContent = template
      .replace('[earliest]', formatter.format(earliest))
      .replace('[latest]', formatter.format(latest));
  }
}

class MonCartProgress extends HTMLElement {
  static #previousByContext = new Map();

  static get observedAttributes() {
    return ['data-progress'];
  }

  connectedCallback() {
    this.#queueAnimation();
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#queueAnimation();
  }

  #queueAnimation() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const fill = this.querySelector('.mon-cart-progress__fill');
      if (!fill) return;

      const context = this.dataset.context || 'drawer';
      const next = Math.max(0, Math.min(1, Number(this.dataset.progress) || 0));
      const previous = MonCartProgress.#previousByContext.get(context) ?? 0;
      MonCartProgress.#previousByContext.set(context, next);

      if (previous === next || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const animation = fill.animate(
        [{ transform: `scaleX(${previous})` }, { transform: `scaleX(${next})` }],
        { duration: 460, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
      );
      animation.onfinish = () => animation.cancel();
    });
  }
}

class MonCartSummary extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const label = this.dataset.checkoutLabel;
    const text = this.querySelector('.cart__checkout-button .button-text');
    if (label && text) text.textContent = label;
  }
}

class MonCartReservationDemo extends HTMLElement {
  connectedCallback() {
    this.remaining = Number(this.dataset.duration) || 180;
    this.render();
    this.interval = window.setInterval(() => {
      this.remaining = this.remaining > 0 ? this.remaining - 1 : Number(this.dataset.duration) || 180;
      this.render();
    }, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  render() {
    const output = this.querySelector('[data-mon-reservation-time]');
    if (!output) return;
    const minutes = Math.floor(this.remaining / 60);
    const seconds = String(this.remaining % 60).padStart(2, '0');
    output.textContent = `${minutes}:${seconds}`;
  }
}

if (!customElements.get('mon-cart-delivery')) customElements.define('mon-cart-delivery', MonCartDelivery);
if (!customElements.get('mon-cart-progress')) customElements.define('mon-cart-progress', MonCartProgress);
if (!customElements.get('mon-cart-summary')) customElements.define('mon-cart-summary', MonCartSummary);
if (!customElements.get('mon-cart-reservation-demo')) {
  customElements.define('mon-cart-reservation-demo', MonCartReservationDemo);
}

const refreshCartEnhancements = () => {
  document.querySelectorAll('mon-cart-delivery').forEach((element) => element.render?.());
  document.querySelectorAll('mon-cart-summary').forEach((element) => element.render?.());
};

const refreshAfterCartMorph = (event) => {
  const refresh = () => {
    requestAnimationFrame(() => requestAnimationFrame(refreshCartEnhancements));
  };

  if (event.promise instanceof Promise) {
    event.promise.then(refresh).catch(() => {});
  } else {
    refresh();
  }
};

if (!window.mmCartRefreshBound) {
  document.addEventListener('shopify:cart:lines-update', refreshAfterCartMorph);
  document.addEventListener('shopify:cart:discount-update', refreshAfterCartMorph);
  window.mmCartRefreshBound = true;
}
